import type {DefinitionOrReference} from '@github-ui/code-nav'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {clsx} from 'clsx'
import {type CSSProperties, memo, type ReactNode} from 'react'

import styles from './HighlightedOverlay.module.css'

export function HighlightedOverlay({
  symbols,
  focusedSymbol,
  lineNumber,
  style,
  isNotUsingWhitespace,
  className,
}: {
  symbols: DefinitionOrReference[]
  focusedSymbol?: DefinitionOrReference
  lineNumber: number
  style?: CSSProperties
  isNotUsingWhitespace?: boolean
  className?: string
}) {
  let startIndex = 0
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const text = symbols.length > 0 ? symbols[0]!.bodyText : ''
  const key = `overlay-${lineNumber}-${style ? 'blob' : 'panel'}-${text}`
  const wrapOption = useCodeViewOptions().codeWrappingOption
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const leadingWhitespace = symbols.length > 0 && !isNotUsingWhitespace ? (symbols[0]!.leadingWhitespace ?? 0) : 0

  // Build the highlighted symbol spans, tracking `startIndex` in the loop body rather than mutating it
  // inside a .map callback (a captured-lambda mutation the React Compiler can't optimize around).
  const symbolSpans: ReactNode[] = []
  for (const symbol of symbols) {
    symbolSpans.push(
      <HighlightedSymbolSpan
        key={`symbol-${symbol.ident.start.line}-${symbol.ident.start.column + leadingWhitespace}`}
        symbol={symbol}
        isFocused={symbol === focusedSymbol}
        text={text}
        startIndex={startIndex}
        leadingWhitespace={leadingWhitespace}
      />,
    )
    startIndex = symbol.ident.end.column + leadingWhitespace
  }

  return (
    <span
      key={key}
      className={clsx(
        styles.overlayWrapper,
        wrapOption.enabled ? styles.overlayWrapperWrap : styles.overlayWrapperNoWrap,
        className,
      )}
      style={style}
    >
      {symbolSpans}
      {
        // Return the rest of the line to wrap properly
        <span className={styles.hiddenText}>{text.substring(startIndex)}</span>
      }
    </span>
  )
}

export function matchElementId(line: number, column: number) {
  return `match-${line}-${column}`
}

const HighlightedSymbolSpan = memo(function HighlightedSymbolSpanInner({
  symbol,
  isFocused,
  text,
  startIndex,
  leadingWhitespace,
}: {
  symbol: DefinitionOrReference
  isFocused: boolean
  text: string
  startIndex: number
  leadingWhitespace: number
}) {
  const start = symbol.ident.start.column + leadingWhitespace
  const end = symbol.ident.end.column + leadingWhitespace
  return (
    <span>
      <span className={styles.hiddenText}>{text.substring(startIndex, start)}</span>
      <span
        className={clsx(styles.highlightBackground, isFocused ? styles.focusedHighlight : styles.unfocusedHighlight)}
      >
        <span className={!isFocused ? styles.hiddenText : undefined} id={matchElementId(symbol.lineNumber, start)}>
          {text.substring(start, end)}
        </span>
      </span>
    </span>
  )
})
